/**
 * Custom hook for fetching and managing month context events
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type { MonthEvent, CreateMonthEventInput } from '@/types/scalingUp';

interface UseMonthEventsResult {
  events: MonthEvent[];
  loading: boolean;
  error: string | null;
  mutationError: string | null;
  addEvent: (input: CreateMonthEventInput) => Promise<MonthEvent | null>;
  deleteEvent: (id: string) => Promise<boolean>;
}

export function useMonthEvents(): UseMonthEventsResult {
  const [events, setEvents] = useState<MonthEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<{ data: MonthEvent[] }>('/scaling-up/events');
        if (!cancelled) {
          setEvents(response.data.data);
        }
      } catch (err) {
        if (!cancelled) {
          try {
            handleApiError(err, 'Failed to load month events');
          } catch (handled) {
            setError(
              handled instanceof Error ? handled.message : 'Failed to load events'
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadEvents();
    return () => { cancelled = true; };
  }, []);

  const addEvent = useCallback(
    async (input: CreateMonthEventInput): Promise<MonthEvent | null> => {
      try {
        setMutationError(null);
        const response = await apiClient.post<{ data: MonthEvent }>(
          '/scaling-up/events',
          input
        );
        const newEvent = response.data.data;
        setEvents((prev) =>
          [...prev, newEvent].sort((a, b) => a.month.localeCompare(b.month))
        );
        return newEvent;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to add event';
        setMutationError(message);
        return null;
      }
    },
    []
  );

  const deleteEvent = useCallback(async (id: string): Promise<boolean> => {
    try {
      setMutationError(null);
      await apiClient.delete(`/scaling-up/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete event';
      setMutationError(message);
      return false;
    }
  }, []);

  return { events, loading, error, mutationError, addEvent, deleteEvent };
}
